import cn from "classnames";
import BookShow from "components/shared/book/show";
import { Transition } from "components/shared/column";
import Loader from "components/shared/loader";
import { defaultMultipleSelectProps, ValueContainer } from "components/shared/select";
import { documentTagGroups } from "helpers/enums";
import { createRequestForUploadFile, getInitialStateFromConfig } from "helpers/form";
import { viewContainer } from "helpers/redux";
import { enumLabel, ymEvent } from "helpers/system";
import { appendCustomDocumentTagGroups } from "helpers/tagGroup";
import { requiredField } from "helpers/validators";
import React, { useEffect } from "react";
import { namesList as counterpartiesList } from "sources/books/cargoOwners";
import { getDocumentTags } from "sources/books/documentTemplates";
import TagsList from "./tagsList";

const config = {
    name: {
        label: "Название",
        inputProps: { name: "name" },
        validators: [requiredField],
        required: true,
    },
    counterpartyIds: {
        ...defaultMultipleSelectProps,
        label: "Контрагент",
        inputProps: { name: "counterpartyIds" },
        url: counterpartiesList,
        propName: "name",
        components: { ValueContainer },
    },
    file: {
        label: "Файл шаблона",
        inputProps: { name: "file" },
    },
    id: {},
    isInUse: {},
    type: {},
    usedTags: {},
};

const initialState = getInitialStateFromConfig(config);

class DocumentTemplatesForm extends BookShow {
    componentDidMount() {
        this.context?.setPrevItemId(Number(this.props.match.params.id));
        const initial = this.props.initialState;
        this.setState({ data: initial, initial });
        getDocumentTags().then((res) => {
            if (res) {
                const documentTags = Object.entries(Object.groupBy(res, (item) => item.group) || {}).map(
                    ([group, tags]) => ({
                        text: enumLabel(documentTagGroups, group),
                        items: tags.map((tag) => ({
                            text: tag.localizedName,
                            id: tag.localizedName,
                        })),
                    }),
                );

                this.setState({ documentTags: appendCustomDocumentTagGroups(documentTags) });
            }
            this.getItem(this.props);
        });
    }

    getItem = ({ source, match }) => {
        const defParams = { isLoading: false, isValid: true, shouldUpdate: false };
        if (match.params.id) {
            source.getItem(match.params).then((data) => {
                data.file ??= undefined;
                this.setState({ data, initial: data, ...defParams });
            });
        } else {
            this.setState(defParams);
        }
    };

    get dropdActions() {
        if (this.props.match.params.id) {
            return [{ text: "Удалить", action: this.actionClose }];
        } else {
            return [];
        }
    }

    apply = () => {
        const { source, match, back } = this.props;
        const { isLoading, shouldUpdate, ...state } = this.state;
        if (!isLoading && shouldUpdate) {
            if (this.isValidForm(state.data)) {
                ymEvent("SaveTemplate");
                this.setState({ isLoading: true, isValid: true });
                let paramsRequest;
                if (this.state.data.file && !this.state.data.file.id) {
                    paramsRequest = createRequestForUploadFile(
                        "file",
                        this.state.data.file,
                        this.state.data.file?.name,
                    );
                } else {
                    paramsRequest = createRequestForUploadFile();
                }
                paramsRequest.body.append("name", state.data.name);
                if (Array.isArray(state.data.counterpartyIds)) {
                    state.data.counterpartyIds.forEach((id, index) => {
                        paramsRequest.body.append(`counterpartyIds[${index}]`, id);
                    });
                }

                if (state.data.id) {
                    paramsRequest.body.append("id", state.data.id);
                }
                return source.apply(paramsRequest).then((res) => {
                    if (res) {
                        match.params.id ? this.update() : back();
                    } else {
                        this.setState({ isLoading: false, isValid: false });
                    }
                });
            }
            this.setState({ isValid: false });
        }
    };

    renderButton = () => {
        const cname = cn("btn btn-brand btn-sm", { disabled: !this.state.shouldUpdate });
        return (
            <>
                <div className="d-flex justify-content-between align-items-center">
                    <div
                        className="btn btn-sm btn-link"
                        onClick={() => this.setState({ showTagsList: !this.state.showTagsList })}
                    >
                        {this.state.showTagsList ? "Скрыть список тегов" : "Показать список тегов"}
                    </div>
                    <div className={cname} onClick={this.apply}>
                        {this.props.match.params.id ? "Обновить" : "Сохранить"}
                    </div>
                </div>
                {this.state.showTagsList && <TagsList documentTags={this.state.documentTags} />}
            </>
        );
    };

    renderHead = () => {
        const { back, title } = this.props;
        return (
            <div className="book-show__head">
                <div className="book-show__close close close--sm" onClick={back} />
                <div className="book-show__title">{title}</div>
                <div className="book-show__head-actions">
                    {!!this.dropdActions.length && (
                        <div className="book-show__dropdown c-dropd ml-3">
                            <div className="c-dropd__btn" onClick={this.openDropd} />
                            {this.renderDropd()}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    renderBody = () => {
        const { config } = this.props;
        if (this.state.isLoading) {
            return <Loader prefix="absolute-center" />;
        }

        const accessUrlLink = this.state.data?.file?.accessUrlLink;
        const inputBottomText = accessUrlLink ? (
            <div className="mt-1">
                <a
                    href={accessUrlLink}
                    download={this.state.data?.file?.originalFileName || this.state.data?.file?.name || "template"}
                    className="link small"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <i className="icon-uEA52-load-point icon-inline pr-1" />
                    Скачать текущий файл шаблона
                </a>
            </div>
        ) : null;

        return (
            <>
                {this.renderField(config.name)}
                {this.renderSelect(config.counterpartyIds)}
                {this.renderFileField({
                    ...config.file,
                    inputBottomText,
                })}
                {this.renderButton()}
            </>
        );
    };

    render() {
        const cname = cn("book-show__body", { "book-show__body--invalid": !this.state.isValid });
        return (
            <Transition dir="full">
                <div className="page-book__show book-show">
                    {this.renderHead()}
                    <div className={cname}>{this.renderBodyWrap()}</div>
                </div>
            </Transition>
        );
    }
}

const ConfiguredShow = (props) => {
    useEffect(() => {
        ymEvent("AddTemplate");
    }, []);

    return <DocumentTemplatesForm {...props} config={config} initialState={initialState} />;
};

export default viewContainer(ConfiguredShow, "document-templates");
